import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminTeam.css";

/* webpack require.context for team member photos */
let TEAM_PHOTOS = {};
try {
  const ctx = require.context(
    "../../assets/uploads/team",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  TEAM_PHOTOS = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* outside CRA — fall back to API URL */
}

function teamPhoto(filename) {
  if (!filename) return null;
  return TEAM_PHOTOS[filename] ?? `${API_BASE}/uploads/team/${filename}`;
}

const SOCIAL_PLATFORMS = [
  { key: "linkedin_url",  label: "LinkedIn",  emoji: "🔗" },
  { key: "twitter_url",   label: "Twitter/X", emoji: "🐦" },
  { key: "instagram_url", label: "Instagram", emoji: "📸" },
  { key: "facebook_url",  label: "Facebook",  emoji: "👥" },
  { key: "whatsapp_number", label: "WhatsApp", emoji: "💬" },
];

const SOCIAL_TAG_CLASS = {
  linkedin_url:   "at-social-tag--linkedin",
  twitter_url:    "at-social-tag--twitter",
  instagram_url:  "at-social-tag--instagram",
  facebook_url:   "at-social-tag--facebook",
  whatsapp_number: "at-social-tag--whatsapp",
};

const EMPTY_FORM = {
  name:            "",
  role:            "",
  team_email:      "",
  contact_number:  "",
  photo_url:       null,   // new File upload
  linkedin_url:    "",
  twitter_url:     "",
  instagram_url:   "",
  facebook_url:    "",
  whatsapp_number: "",
  order:           999,
  active:          1,
};

/* ─────────────────────────────────
   Sub-components
   ───────────────────────────────── */
function StatusPill({ active }) {
  return (
    <span className={`at-status-pill ${Number(active) === 1 ? "at-status-pill--active" : "at-status-pill--inactive"}`}>
      {Number(active) === 1 ? "● Active" : "○ Inactive"}
    </span>
  );
}

function SocialTags({ member }) {
  const tags = SOCIAL_PLATFORMS.filter(({ key }) => member[key] && String(member[key]).trim());
  if (!tags.length) return null;
  return (
    <div className="at-card__socials">
      {tags.map(({ key, label, emoji }) => (
        <span key={key} className={`at-social-tag ${SOCIAL_TAG_CLASS[key]}`}>
          {emoji} {label}
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────
   Member Card
   ───────────────────────────────── */
function MemberCard({ member, onEdit, onDelete, onToggle }) {
  const photoSrc = teamPhoto(member.photo_url);
  return (
    <div className={`at-card ${Number(member.active) !== 1 ? "at-card--inactive" : ""}`}>
      <div className="at-card__photo-wrap">
        {photoSrc ? (
          <img src={photoSrc} alt={member.name} className="at-card__photo" />
        ) : (
          <div className="at-card__photo-placeholder">👤</div>
        )}
        <div className="at-card__status-badge">
          <StatusPill active={member.active} />
        </div>
      </div>

      <div className="at-card__body">
        <h3 className="at-card__name">{member.name}</h3>
        <p className="at-card__role">{member.role}</p>

        {(member.team_email || member.contact_number) && (
          <div className="at-card__contact">
            {member.team_email && (
              <span className="at-card__contact-row">
                <span className="at-card__contact-icon">✉</span>
                {member.team_email}
              </span>
            )}
            {member.contact_number && (
              <span className="at-card__contact-row">
                <span className="at-card__contact-icon">📞</span>
                {member.contact_number}
              </span>
            )}
          </div>
        )}

        <SocialTags member={member} />
      </div>

      <div className="at-card__footer">
        <span className="at-card__order-badge">
          # {member.order ?? 999}
        </span>
        <div className="at-card__actions">
          <button
            className={`at-icon-btn ${Number(member.active) === 1 ? "at-icon-btn--toggle-on" : "at-icon-btn--toggle-off"}`}
            title={Number(member.active) === 1 ? "Deactivate" : "Activate"}
            onClick={() => onToggle(member)}
          >
            {Number(member.active) === 1 ? "✓" : "○"}
          </button>
          <button
            className="at-icon-btn at-icon-btn--edit"
            title="Edit"
            onClick={() => onEdit(member)}
          >
            ✎
          </button>
          <button
            className="at-icon-btn at-icon-btn--delete"
            title="Delete"
            onClick={() => onDelete(member)}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────
   Member Modal (Add / Edit)
   ───────────────────────────────── */
function MemberModal({ member, onClose, onSaved }) {
  const isEdit = !!member?.id;
  const [form, setForm]       = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [clearPhoto, setClearPhoto] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const fileRef = useRef(null);

  /* populate form when editing */
  useEffect(() => {
    if (member) {
      setForm({
        name:            member.name            || "",
        role:            member.role            || "",
        team_email:      member.team_email      || "",
        contact_number:  member.contact_number  || "",
        photo_url:       null,
        linkedin_url:    member.linkedin_url    || "",
        twitter_url:     member.twitter_url     || "",
        instagram_url:   member.instagram_url   || "",
        facebook_url:    member.facebook_url    || "",
        whatsapp_number: member.whatsapp_number || "",
        order:           member.order           ?? 999,
        active:          Number(member.active) === 1 ? 1 : 0,
      });
      setPreview(member.photo_url ? teamPhoto(member.photo_url) : null);
      setClearPhoto(false);
    } else {
      setForm(EMPTY_FORM);
      setPreview(null);
      setClearPhoto(false);
    }
  }, [member]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setError("");
  };

  const handleFile = (file) => {
    if (!file) return;
    set("photo_url", file);
    setClearPhoto(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClearPhoto = () => {
    set("photo_url", null);
    setPreview(null);
    setClearPhoto(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async () => {
    setError("");
    if (!form.name.trim()) return setError("Name is required.");
    if (!form.role.trim()) return setError("Role / position is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name",            form.name.trim());
      fd.append("role",            form.role.trim());
      fd.append("team_email",      form.team_email      || "");
      fd.append("contact_number",  form.contact_number  || "");
      fd.append("linkedin_url",    form.linkedin_url    || "");
      fd.append("twitter_url",     form.twitter_url     || "");
      fd.append("instagram_url",   form.instagram_url   || "");
      fd.append("facebook_url",    form.facebook_url    || "");
      fd.append("whatsapp_number", form.whatsapp_number || "");
      fd.append("order",           form.order);
      fd.append("active",          form.active);
      if (form.photo_url) fd.append("photo_url", form.photo_url);
      if (clearPhoto)     fd.append("clear_photo", "1");

      if (isEdit) {
        await apiFetch(`/api/admin/team/${member.id}`, { method: "PUT", body: fd });
      } else {
        await apiFetch("/api/admin/team", { method: "POST", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* close on backdrop click */
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="at-modal-backdrop" onClick={handleBackdrop}>
      <div className="at-modal">
        <div className="at-modal__header">
          <h2 className="at-modal__title">
            {isEdit ? `Edit — ${member.name}` : "Add Team Member"}
          </h2>
          <button className="at-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="at-modal__body">
          {error && <div className="at-alert at-alert--error" style={{ marginBottom: 16 }}>⚠ {error}</div>}

          {/* ── Identity ── */}
          <div className="at-msec">
            <h4 className="at-msec__title">Identity</h4>
            <div className="at-two-col">
              <div className="at-field">
                <label className="at-field__label">Name <span className="at-req">*</span></label>
                <input
                  className="at-input"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Jane Smith"
                />
              </div>
              <div className="at-field">
                <label className="at-field__label">Role / Position <span className="at-req">*</span></label>
                <input
                  className="at-input"
                  value={form.role}
                  onChange={(e) => set("role", e.target.value)}
                  placeholder="Service Manager"
                />
              </div>
            </div>
            <div className="at-two-col">
              <div className="at-field">
                <label className="at-field__label">Display Order</label>
                <p className="at-field__hint">Lower numbers appear first.</p>
                <input
                  className="at-input"
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="at-field" style={{ justifyContent: "flex-end", paddingBottom: 2 }}>
                <label className="at-field__label">Status</label>
                <label className="at-check" style={{ marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>

          {/* ── Contact ── */}
          <div className="at-msec">
            <h4 className="at-msec__title">Contact Details</h4>
            <div className="at-two-col">
              <div className="at-field">
                <label className="at-field__label">Email Address</label>
                <input
                  className="at-input"
                  type="email"
                  value={form.team_email}
                  onChange={(e) => set("team_email", e.target.value)}
                  placeholder="jane@company.co.za"
                />
              </div>
              <div className="at-field">
                <label className="at-field__label">Contact Number</label>
                <input
                  className="at-input"
                  type="tel"
                  value={form.contact_number}
                  onChange={(e) => set("contact_number", e.target.value)}
                  placeholder="+27 82 000 0000"
                />
              </div>
            </div>
          </div>

          {/* ── Photo ── */}
          <div className="at-msec">
            <h4 className="at-msec__title">Profile Photo</h4>
            <div className="at-photo-wrap">
              <div
                className={`at-photo-drop ${preview ? "at-photo-drop--has-photo" : ""}`}
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="at-photo-drop__preview" />
                ) : (
                  <div className="at-photo-drop__placeholder">
                    <span className="at-photo-drop__icon">👤</span>
                    <span className="at-photo-drop__text">Click to upload photo</span>
                    <span className="at-photo-drop__sub">PNG, JPG, WebP — max 10 MB</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              <div className="at-photo-actions">
                <button type="button" className="at-image-btn" onClick={() => fileRef.current?.click()}>
                  {preview ? "Change Photo" : "Upload Photo"}
                </button>
                {preview && (
                  <button type="button" className="at-image-btn at-image-btn--remove" onClick={handleClearPhoto}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Social Links ── */}
          <div className="at-msec">
            <h4 className="at-msec__title">Social Links</h4>
            <div className="at-two-col">
              <div className="at-field">
                <label className="at-field__label">🔗 LinkedIn URL</label>
                <input
                  className="at-input"
                  value={form.linkedin_url}
                  onChange={(e) => set("linkedin_url", e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div className="at-field">
                <label className="at-field__label">🐦 Twitter / X URL</label>
                <input
                  className="at-input"
                  value={form.twitter_url}
                  onChange={(e) => set("twitter_url", e.target.value)}
                  placeholder="https://twitter.com/username"
                />
              </div>
              <div className="at-field">
                <label className="at-field__label">📸 Instagram URL</label>
                <input
                  className="at-input"
                  value={form.instagram_url}
                  onChange={(e) => set("instagram_url", e.target.value)}
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div className="at-field">
                <label className="at-field__label">👥 Facebook URL</label>
                <input
                  className="at-input"
                  value={form.facebook_url}
                  onChange={(e) => set("facebook_url", e.target.value)}
                  placeholder="https://facebook.com/username"
                />
              </div>
              <div className="at-field">
                <label className="at-field__label">💬 WhatsApp Number</label>
                <p className="at-field__hint">Include country code, e.g. 27821234567</p>
                <input
                  className="at-input"
                  value={form.whatsapp_number}
                  onChange={(e) => set("whatsapp_number", e.target.value)}
                  placeholder="27821234567"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="at-modal__footer">
          <div />
          <div className="at-modal__footer-right">
            <button className="at-btn at-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="at-btn at-btn--save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Member"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────
   Confirm Delete Dialog
   ───────────────────────────────── */
function ConfirmDialog({ member, onCancel, onConfirm, deleting }) {
  return (
    <div className="at-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="at-confirm">
        <div className="at-confirm__icon">🗑</div>
        <h3 className="at-confirm__title">Delete Team Member?</h3>
        <p className="at-confirm__msg">
          This will permanently remove <strong>{member.name}</strong> and delete their photo from the server. This action cannot be undone.
        </p>
        <div className="at-confirm__actions">
          <button className="at-btn at-btn--cancel" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className="at-btn at-btn--danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminTeam() {
  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  /* filters */
  const [search,     setSearch]     = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [sort,       setSort]       = useState("order_asc");

  /* modals */
  const [editTarget,   setEditTarget]   = useState(null);   // null = closed, {} = new, member = edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Load members ── */
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort, limit: 200 });
    if (search)       params.set("search", search);
    if (filterActive !== "") params.set("active", filterActive);

    apiFetch(`/api/admin/team?${params}`)
      .then((data) => setMembers(data.members || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, filterActive, sort]);

  useEffect(() => { load(); }, [load]);

  /* ── Stats ── */
  const totalActive   = members.filter((m) => Number(m.active) === 1).length;
  const totalInactive = members.filter((m) => Number(m.active) !== 1).length;

  /* ── Toggle active ── */
  const handleToggle = async (member) => {
    setError(""); setSuccess("");
    try {
      const res = await apiFetch(`/api/admin/team/${member.id}/toggle`, { method: "PUT" });
      setSuccess(res.message || "Status updated.");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/team/${deleteTarget.id}`, { method: "DELETE" });
      setSuccess(`${deleteTarget.name} deleted.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* ── After modal save ── */
  const handleSaved = () => {
    setEditTarget(null);
    setSuccess("Team member saved successfully.");
    load();
  };

  if (loading) {
    return (
      <AdminLayout pageTag="CONTENT" pageTitle="Team">
        <div className="at-skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Team">

      {/* ── Intro bar ── */}
      <div className="at-intro-bar">
        <div className="at-intro-bar__left">
          <span className="at-intro-bar__icon">👥</span>
          <div>
            <h2 className="at-intro-bar__title">Team Members</h2>
            <p className="at-intro-bar__sub">
              Add, edit, and manage the people displayed on your About Us and Team pages.
            </p>
          </div>
        </div>
        <div className="at-intro-bar__right">
          <span className="at-stat-pill">
            <span className="at-stat-pill__num">{totalActive}</span> Active
          </span>
          <span className="at-stat-pill">
            <span className="at-stat-pill__num">{totalInactive}</span> Inactive
          </span>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="at-alert at-alert--error">⚠ {error}</div>}
      {success && <div className="at-alert at-alert--success">✓ {success}</div>}

      {/* ── Toolbar ── */}
      <div className="at-toolbar">
        <div className="at-toolbar__left">
          <div className="at-search-wrap">
            <span className="at-search-icon">⌕</span>
            <input
              className="at-search"
              type="text"
              placeholder="Search members…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="at-filter-select"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <select
            className="at-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="name_asc">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <button className="at-btn-add" onClick={() => setEditTarget({})}>
          + Add Member
        </button>
      </div>

      {/* ── Grid ── */}
      <div className="at-grid">
        {members.length === 0 ? (
          <div className="at-empty">
            <div className="at-empty__icon">👤</div>
            <h3 className="at-empty__title">No Team Members Found</h3>
            <p className="at-empty__sub">
              {search || filterActive !== ""
                ? "Try adjusting your filters."
                : "Click \"Add Member\" to introduce your team."}
            </p>
          </div>
        ) : (
          members.map((m) => (
            <MemberCard
              key={m.id}
              member={m}
              onEdit={(mem) => setEditTarget(mem)}
              onDelete={(mem) => setDeleteTarget(mem)}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>

      {/* ── Modal ── */}
      {editTarget !== null && (
        <MemberModal
          member={Object.keys(editTarget).length === 0 ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <ConfirmDialog
          member={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

    </AdminLayout>
  );
}